import React, { useState, useMemo, useRef, useEffect } from "react";
import { resultsApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./Results.scss";

const COURSES = ["B.A.", "B.Sc.", "B.Com.", "B.B.A.", "B.C.A.", "B.Tech.", "M.A.", "M.Sc.", "M.Com.", "M.B.A.", "M.C.A.", "M.Tech.", "Ph.D."];
const SEMESTERS = ["Semester 1", "Semester 2", "Semester 3", "Semester 4", "Semester 5", "Semester 6", "Semester 7", "Semester 8"];
const ACADEMIC_YEARS = ["2022-23", "2023-24", "2024-25", "2025-26", "2026-27"];
const EXAM_TYPES = ["Internal Assessment", "Mid-Term Exam", "End-Term Exam", "Practical Exam", "Supplementary Exam", "Project Evaluation"];
const RESULT_STATUS = {
  published: { label: "Published", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  draft: { label: "Draft", color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0" },
  under_review: { label: "Under Review", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  withheld: { label: "Withheld", color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
};

const defaultForm = {
  _id: null, Result_Code: "", Course: "B.Sc.", Semester: "Semester 1",
  Academic_Year: "2025-26", Exam_Type: "End-Term Exam",
  Declared_Date: new Date().toISOString().slice(0, 10),
  Total_Students: "", Passed_Students: "", Failed_Students: "",
  Status: "draft", Remarks: "", Subjects: [],
};

const Results = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [formData, setFormData] = useState(defaultForm);
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchResults(); }, []);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await apiClient(resultsApi);
      const result = await response.json();
      if (response.ok) { setResults(result.data || []); }
    } catch (error) {
      console.error("Error fetching results:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onClose = () => setDropdownOpenId(null);
    window.addEventListener("scroll", onClose, true);
    return () => window.removeEventListener("scroll", onClose, true);
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 200 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => { setFormData(defaultForm); setModalType("add"); };
  const openEditModal = (r) => { setFormData({ ...r }); setModalType("edit"); setDropdownOpenId(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? resultsApi : `${resultsApi}/${formData._id}`;
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(formData),
      });
      if (response.ok) { fetchResults(); setModalType(""); }
    } catch (error) {
      console.error("Error saving result:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const item = results.find(r => r._id === id);
      await apiClient(`${resultsApi}/${id}`, {
        method: "PUT",
        body: JSON.stringify({ ...item, Status: status }),
      });
      fetchResults();
      setDropdownOpenId(null);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const filtered = results.filter(r => (r.Course || "").toLowerCase().includes(searchText.toLowerCase()) || (r.Result_Code || "").toLowerCase().includes(searchText.toLowerCase()));

  return (
    <div className="res-page">
      <div className="res-header">
        <div className="res-header-left"><h1>Results Management</h1><p>Declare and manage examination results.</p></div>
        <button className="res-add-btn" onClick={openAddModal}>Declare Result</button>
      </div>

      <div className="res-toolbar">
        <input type="text" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="res-grid">
        {filtered.map((r) => {
          const status = RESULT_STATUS[r.Status] || RESULT_STATUS.draft;
          return (
            <div key={r._id} className="res-card">
              <div className="rc-head">
                <div><strong>{r.Result_Code}</strong><p>{r.Academic_Year} | {r.Exam_Type}</p></div>
                <div className="rc-actions"><span className="res-status-badge" style={{ color: status.color, background: status.bg, border: `1px solid ${status.border}` }}>{status.label}</span><button className="dots-btn" onClick={(e) => handleDotsClick(e, r._id)}>⋮</button></div>
              </div>
              <div className="rc-course">{r.Course}</div>
              <div className="rc-sem">{r.Semester}</div>
              <div className="rc-footer"><span>📅 {r.Declared_Date}</span><button onClick={() => openEditModal(r)}>Edit</button></div>
            </div>
          );
        })}
      </div>

      {dropdownOpenId && (
        <div ref={dropdownRef} className="res-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}>
          <button onClick={() => updateStatus(dropdownOpenId, "published")}>Publish</button>
          <button onClick={() => updateStatus(dropdownOpenId, "draft")}>Draft</button>
          <button onClick={() => { /* delete logic */ }}>Delete</button>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="res-overlay" onClick={() => setModalType("")}>
          <div className="res-modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSave}>
              <div className="rm-fgroup full"><label>Course *</label><select value={formData.Course} onChange={(e) => setFormData({ ...formData, Course: e.target.value })}>{COURSES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div className="rm-row">
                <div className="rm-fgroup"><label>Semester *</label><select value={formData.Semester} onChange={(e) => setFormData({ ...formData, Semester: e.target.value })}>{SEMESTERS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="rm-fgroup"><label>Exam Type *</label><select value={formData.Exam_Type} onChange={(e) => setFormData({ ...formData, Exam_Type: e.target.value })}>{EXAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
              </div>
              <div className="rm-row">
                <div className="rm-fgroup"><label>Year *</label><select value={formData.Academic_Year} onChange={(e) => setFormData({ ...formData, Academic_Year: e.target.value })}>{ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                <div className="rm-fgroup"><label>Code *</label><input type="text" required value={formData.Result_Code} onChange={(e) => setFormData({ ...formData, Result_Code: e.target.value })} /></div>
              </div>
              <div className="rm-fgroup full"><label>Remarks</label><textarea value={formData.Remarks} onChange={(e) => setFormData({ ...formData, Remarks: e.target.value })} /></div>
              <div className="rm-footer"><button type="button" onClick={() => setModalType("")}>Cancel</button><button type="submit">Save</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Results;